/**
 * Computes projection, dimensions, and geography for the regional map.
 * Filters GeoJSON to the active state/layer, derives bounds, and produces
 * stable projection center/scale plus canvas dimensions for the map renderer.
 * Author: bb
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { toUiScaleFactor } from "@/utils/uiScale";
import {
  ADMINISTRATIVE_DISTRICTS_TOPOJSON_URL,
  coords,
  DISTRICTS_TOPOJSON_URL,
  GERMANY_BOUNDS,
  getGeographyBounds,
  getLayerGeography,
  MapLayerId,
  LayerGeographies,
  FEDERAL_STATES_TOPOJSON_URL,
  POSTAL_CODES_COARSE_TOPOJSON_URL,
  POSTAL_CODES_MEDIUM_TOPOJSON_URL,
  POSTAL_CODES_FULL_TOPOJSON_URL,
} from "./mapHelpers";
import { resolveMapResponsive } from "./map-responsive";
import { getZoomBoost } from "./map-constants";
import { loadTopojson } from "@/utils/loadTopojson";
import type { UseViewportResult } from "./RegionalFilter.types";

const isDevEnvironment =
  typeof import.meta !== "undefined" ? import.meta.env?.DEV ?? false : false;
// Flip to true when you need to inspect viewport calculations locally.
const viewportDebugEnabled = false;

const viewportLog = (...args: any[]) => {
  if (!isDevEnvironment || !viewportDebugEnabled) return;
  console.log("[Viewport]", ...args);
};

/**
 * Derives geography, projection, and responsive canvas sizing from selection state.
 * Keeps the projection centered on the active state's bounds with a conservative
 * zoom boost tuned per layer; falls back to country view when no state is active.
 */
export function useViewport(
  detailLayer: MapLayerId,
  activeState: string | null
): UseViewportResult {
  const { uiScale } = useApp();
  const [layers, setLayers] = useState<LayerGeographies>({
    federalStates: null,
    administrativeDistricts: null,
    districts: null,
    postalCodes: null,
    postalCodesCoarse: null,
    postalCodesMedium: null,
    postalCodesFull: null,
  });
  const requestedLayersRef = useRef<Set<string>>(new Set());

  const responsive = useMemo(() => {
    if (typeof window === "undefined") {
      return resolveMapResponsive();
    }
    const factor = toUiScaleFactor(uiScale);
    const effectiveWidth =
      Number.isFinite(factor) && factor > 0
        ? window.innerWidth / factor
        : window.innerWidth;
    return resolveMapResponsive(effectiveWidth);
  }, [uiScale]);

  // Always load federal states first; everything else is loaded lazily per active detail layer.
  useEffect(() => {
    if (layers.federalStates || requestedLayersRef.current.has("federalStates")) {
      return;
    }
    requestedLayersRef.current.add("federalStates");
    let cancelled = false;
    loadTopojson(FEDERAL_STATES_TOPOJSON_URL).then((data) => {
      if (cancelled) return;
      setLayers((prev) => ({ ...prev, federalStates: data }));
    });
    return () => {
      cancelled = true;
    };
  }, [layers.federalStates]);

  useEffect(() => {
    if (detailLayer !== "administrativeDistricts") return;
    if (
      layers.administrativeDistricts ||
      requestedLayersRef.current.has("administrativeDistricts")
    ) {
      return;
    }
    requestedLayersRef.current.add("administrativeDistricts");
    let cancelled = false;
    loadTopojson(ADMINISTRATIVE_DISTRICTS_TOPOJSON_URL).then((data) => {
      if (cancelled) return;
      setLayers((prev) => ({ ...prev, administrativeDistricts: data }));
    });
    return () => {
      cancelled = true;
    };
  }, [detailLayer, layers.administrativeDistricts]);

  useEffect(() => {
    if (detailLayer !== "districts") return;
    if (layers.districts || requestedLayersRef.current.has("districts")) {
      return;
    }
    requestedLayersRef.current.add("districts");
    let cancelled = false;
    loadTopojson(DISTRICTS_TOPOJSON_URL).then((data) => {
      if (cancelled) return;
      setLayers((prev) => ({ ...prev, districts: data }));
    });
    return () => {
      cancelled = true;
    };
  }, [detailLayer, layers.districts]);

  useEffect(() => {
    if (detailLayer !== "postalCodes") return;
    let cancelled = false;

    if (
      !layers.postalCodesCoarse &&
      !requestedLayersRef.current.has("postalCodesCoarse")
    ) {
      requestedLayersRef.current.add("postalCodesCoarse");
      loadTopojson(POSTAL_CODES_COARSE_TOPOJSON_URL).then((data) => {
        if (cancelled) return;
        setLayers((prev) => ({ ...prev, postalCodesCoarse: data }));
      });
    }

    if (
      !layers.postalCodesMedium &&
      !requestedLayersRef.current.has("postalCodesMedium")
    ) {
      requestedLayersRef.current.add("postalCodesMedium");
      loadTopojson(POSTAL_CODES_MEDIUM_TOPOJSON_URL).then((data) => {
        if (cancelled) return;
        setLayers((prev) => ({
          ...prev,
          postalCodes: data,
          postalCodesMedium: data,
        }));
      });
    }

    return () => {
      cancelled = true;
    };
  }, [detailLayer, layers.postalCodesCoarse, layers.postalCodesMedium]);

  // Delay full-detail postal loading until postal view is actually active.
  useEffect(() => {
    if (detailLayer !== "postalCodes") return;
    if (layers.postalCodesFull || requestedLayersRef.current.has("postalCodesFull")) {
      return;
    }
    requestedLayersRef.current.add("postalCodesFull");
    let cancelled = false;
    const timeout = window.setTimeout(() => {
      loadTopojson(POSTAL_CODES_FULL_TOPOJSON_URL).then((data) => {
        if (cancelled) return;
        setLayers((prev) => ({ ...prev, postalCodesFull: data }));
      });
    }, 750);
    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
    };
  }, [detailLayer, layers.postalCodesFull]);

  const width = responsive.isMobile ? 700 : responsive.isTablet ? 800 : 900;
  const height = responsive.isMobile ? 700 : responsive.isTablet ? 800 : 900;

  const memoizedLayers = useMemo(
    () => ({
      federalStates: layers.federalStates,
      administrativeDistricts: layers.administrativeDistricts,
      districts: layers.districts,
      postalCodes: layers.postalCodes,
      postalCodesCoarse: layers.postalCodesCoarse,
      postalCodesMedium: layers.postalCodesMedium,
      postalCodesFull: layers.postalCodesFull,
    }),
    [
      layers.administrativeDistricts,
      layers.districts,
      layers.federalStates,
      layers.postalCodes,
      layers.postalCodesCoarse,
      layers.postalCodesMedium,
      layers.postalCodesFull,
    ]
  );

  const geography = useMemo(
    () => getLayerGeography(detailLayer, activeState, memoizedLayers),
    [activeState, detailLayer, memoizedLayers]
  );
  useEffect(() => {
    const featureCount = Array.isArray((geography as any)?.features)
      ? (geography as any).features.length
      : null;
    const featureNames =
      Array.isArray((geography as any)?.features) &&
      (geography as any).features
        .slice(0, 5)
        .map((f: any) => f?.properties?.name ?? f?.properties?.NAME_1)
        .filter(Boolean);
    viewportLog("geography", {
      detailLayer,
      activeState,
      featureCount,
      featureNames,
    });
  }, [activeState, detailLayer, geography]);
  const mapBounds = useMemo(() => getGeographyBounds(geography), [geography]);

  const { projectionCenter, projectionScale } = useMemo(() => {
    const baseScale = activeState ? responsive.scale * 0.85 : responsive.scale;
    const defaultCenter = coords(11.1, responsive.centerLat);
    if (!activeState || !mapBounds) {
      return { projectionCenter: defaultCenter, projectionScale: baseScale };
    }

    const baseBounds = GERMANY_BOUNDS;
    const width = Math.max(mapBounds.maxLon - mapBounds.minLon, 0.0001);
    const height = Math.max(mapBounds.maxLat - mapBounds.minLat, 0.0001);
    const baseWidth = Math.max(baseBounds.maxLon - baseBounds.minLon, 0.0001);
    const baseHeight = Math.max(baseBounds.maxLat - baseBounds.minLat, 0.0001);

    const widthRatio = baseWidth / width;
    const heightRatio = baseHeight / height;
    const zoomBoost = getZoomBoost(detailLayer, activeState);
    const ratio = Math.min(widthRatio, heightRatio) * zoomBoost;

    const adjustedScale = Math.min(10000, Math.max(0.1, baseScale * ratio));

    viewportLog("projection", {
      activeState,
      detailLayer,
      bounds: mapBounds,
      baseBounds,
      widthRatio,
      heightRatio,
      zoomBoost,
      ratio,
      adjustedScale,
    });

    return {
      projectionCenter: coords(
        (mapBounds.minLon + mapBounds.maxLon) / 2,
        (mapBounds.minLat + mapBounds.maxLat) / 2
      ),
      projectionScale: adjustedScale,
    };
  }, [activeState, detailLayer, mapBounds, responsive.centerLat, responsive.scale]);

  const zoomGroupKey = useMemo(() => {
    const [centerLon, centerLatValue] = projectionCenter;
    viewportLog("zoomGroupKey", {
      activeState,
      detailLayer,
      centerLon,
      centerLatValue,
      projectionScale,
    });
    return `${
      activeState ?? "country"
    }-${detailLayer}-${centerLon}-${centerLatValue}-${projectionScale}`;
  }, [activeState, detailLayer, projectionCenter, projectionScale]);

  return {
    width,
    height,
    geography,
    allLayers: memoizedLayers,
    projectionCenter,
    projectionScale,
    zoomGroupKey,
  };
}
